import { Badge, BadgeVariant, Box, Button, Modal, TextareaInput } from '@palmetto/palmetto-components';
import { useFormikContext } from 'formik';
import { ChangeEvent, useCallback } from 'react';

import { OrganizationStatus, OrganizationStatusLabels } from 'types';

const statusToTagColor = (status: OrganizationStatus): BadgeVariant => {
  switch (status) {
    case OrganizationStatus.active:
      return 'primary';
    case OrganizationStatus.offBoarded:
      return 'danger';
    case OrganizationStatus.noNewSales:
      return 'warning';
    case OrganizationStatus.readOnly:
      return 'info';
    default:
      return 'default';
  }
};

interface ChangeOrganizationStatusModalProps {
  isOpen: boolean;
  fromStatus: OrganizationStatus;
  toStatus: OrganizationStatus;
  onCancel: () => void;
  onConfirm: (description: string) => void;
}

export const ChangeOrganizationStatusModal = ({
  isOpen,
  fromStatus,
  toStatus,
  onCancel,
  onConfirm,
}: ChangeOrganizationStatusModalProps) => {
  const { values, setFieldValue } = useFormikContext<any>();

  const handleDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      setFieldValue('description', event.target.value);
    },
    [setFieldValue],
  );

  const handleCancel = useCallback(() => {
    setFieldValue('description', '');
    onCancel();
  }, [onCancel, setFieldValue]);

  const description: string = values.description ?? '';

  const handleConfirm = useCallback(() => {
    onConfirm(description.trim());
    setFieldValue('description', '');
  }, [onConfirm, setFieldValue, description]);

  const isConfirmDisabled = description.trim() === '';

  return (
    <Modal isOpen={isOpen} maxWidth="4xl" ariaLabelledBy="changeOrgStatusHeader" onDismiss={handleCancel}>
      <Modal.Header id="changeOrgStatusHeader" title="Change Organization Status" onDismiss={handleCancel} />
      <Modal.Body background="secondary" childGap="lg">
        <Box direction="row" alignItems="center" childGap="sm">
          <Box direction="row" alignItems="center" gap="sm">
            From:
            <Badge
              message={OrganizationStatusLabels[fromStatus] ?? OrganizationStatusLabels.unknown}
              variant={statusToTagColor(fromStatus)}
              tone="light"
            />
          </Box>
          <Box>
            <span>&rarr;</span>
          </Box>
          <Box direction="row" alignItems="center" gap="sm">
            To:
            <Badge
              message={OrganizationStatusLabels[toStatus as OrganizationStatus] ?? OrganizationStatusLabels.unknown}
              variant={statusToTagColor(toStatus)}
            />
          </Box>
        </Box>
        <Box>
          <TextareaInput
            id="statusChangeDescription"
            label="Description"
            placeholder="Describe the reason for this status change"
            value={description}
            onChange={handleDescriptionChange}
            isRequired
            rows={3}
          />
        </Box>
      </Modal.Body>
      <Modal.Footer>
        <Button as="button" type="button" onClick={handleCancel} variant="secondary" tone="neutral" size="md">
          Cancel
        </Button>
        <Button as="button" size="md" type="button" onClick={handleConfirm} disabled={isConfirmDisabled}>
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
